import React, { Component } from 'react';

import _ from 'underscore';
import { titleize } from 'underscore.string';
import { getRegionLinesOfBusiness } from 'config/lines-of-business';
import CustomersAPIUtils from 'utils/CustomersAPIUtils';
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  Button,
} from '@cargomatic/ui';
import Alert from 'components/ui/Alert';
import AccountContacts from 'components/v2/contacts';
import Container from 'components/v2/container';
import Multiselect from 'components/v2/multi-select';
import SessionStore from 'stores/SessionStore';
import ToastStore, { showToast } from 'stores/ToastStore';
import CustomersActionCreators from 'actions/CustomersActionCreators';
import AssetsConfig from 'config/assets'; // only used for defaultPhotoFilename
import { withTranslation } from 'react-i18next';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import PhoneField from 'components/v2/forms/PhoneField';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import AddressForm from 'components/modules/carrier/AddressForm';
import CargomaticInput from 'components/v2/input';
import ChangePasswordModal from '../user/change-password-modal';
import UserWebAPIUtils from '../../../utils/UserWebAPIUtils';
import CarrierAssociatedAccounts from '../user/carrier/CarrierAssociatedAccounts';
import Sidebar from './Sidebar';
import { AvatarWithUpload } from '../user/AvatarWithUpload';
import { getAllMarkets, buildMarketMenuItems } from 'utils/MarketAPIUtils';
import { isNil } from 'lodash';

function onUpdatePassword(newPassword) {
  CustomersActionCreators.updatePassword(
    SessionStore.getUser()._id,
    newPassword,
  );
}

function handleAccessRequest(emails) {
  return UserWebAPIUtils.createAccountAccessRequest(
    SessionStore.getUser()._id,
    emails,
  );
}

class CarrierCenter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      allMarkets: [],
      markets: [],
      allLinesOfBusiness: [],
      linesOfBusiness: [],
      service_type: [],
      name: '',
      email: '',
      dot_number: '',
      vat_number: '',
      primary_scac_code: '',
      hides_prices_from_drivers: false,
      contacts: [],
      first_name: '',
      last_name: '',
      phone: '',
      phone_extension: '',
      picture: AssetsConfig.defaultPhotoFilename,
      street: '',
      city: '',
      state: '',
      zipcode: '',
      international_address: '',
      province: '',
      district: '',
      sub_district: '',
      post_code: '',
      country: '',
      errors: {},
      showChangePasswordModal: false,
      notification: {},
      referral_code: '',
      userId: SessionStore.getUser()._id,
    };
  }

  async componentDidMount() {
    this.populateForm();
    this.loadMarkets();
    SessionStore.addChangeListener(this.handleStoreChange);
    ToastStore.addChangeListener(this.handleCarrierUpdated);
  }

  componentWillUnmount() {
    SessionStore.removeChangeListener(this.handleStoreChange);
    ToastStore.removeChangeListener(this.handleCarrierUpdated);
  }

  carrierHasMarket(marketId) {
    const { markets } = this.state;
    return !!_.findWhere(markets, { _id: marketId });
  }

  loadMarkets = async () => {
    try {
      const allMarkets = await getAllMarkets();
      this.setState({ allMarkets });
    } catch (e) {
      this.setState({ allMarkets: [] });
      showToast(this.props.t('LOAD_MARKETS_FAILED'), e.message, 'error');
    }
  };

  handlePhoneChange = (phone) => {
    this.setState({ phone });
  };

  getHandleInputChange = (field) => {
    return (e) => {
      const value = e.target?.value || undefined;
      this.setState({
        [field]: value,
      });
    };
  };

  getHandleCheckboxChange(field) {
    return () => {
      this.setState((prevState) => ({
        [field]: !prevState[field],
      }));
    };
  }

  getValidationState = (field) => {
    if (Object.prototype.hasOwnProperty.call(this.state.errors, field)) {
      return 'error';
    }
    return null;
  };

  async populateForm() {
    const res = await CustomersAPIUtils.getCarrierNonAdmin(this.state.userId);
    if (!res || !res.entity) {
      return;
    }
    const carrier = res.entity;

    const {
      name,
      email,
      dot_number,
      vat_number,
      primary_scac_code,
      hides_prices_from_drivers,
      first_name,
      last_name,
      phone,
      phone_extension,
      picture,
      address = {},
      referral_code,
      carrier_marketing_identifier,
      contacts,
      markets,
      linesOfBusiness,
    } = carrier;

    const {
      street = '',
      city = '',
      state = '',
      zipcode = '',
      international_address = '',
      province = '',
      district = '',
      sub_district = '',
      post_code = '',
      country = '',
    } = address;
    const LOBs = getRegionLinesOfBusiness().map((lob) => {
      return { ...lob, selected: linesOfBusiness.includes(lob.slug) };
    });

    this.setState({
      name,
      email,
      dot_number,
      vat_number,
      primary_scac_code,
      hides_prices_from_drivers,
      first_name,
      last_name,
      phone,
      phone_extension,
      picture,
      street,
      city,
      state,
      zipcode,
      international_address,
      province,
      district,
      sub_district,
      post_code,
      country,
      referral_code,
      carrier_marketing_identifier,
      contacts,
      markets: (markets ?? []).filter((m) => !isNil(m)),
      linesOfBusiness: LOBs.filter((lob) => lob.selected),
      allLinesOfBusiness: LOBs,
    });
  }

  validate() {
    const errors = {};
    const { name, first_name, last_name, phone } = this.state;
    const { t } = this.props;

    if (!name) {
      errors.name = t('NAME_REQUIRED_ERROR');
    }

    if (!first_name) {
      errors.first_name = t('FIRST_NAME_REQUIRED');
    }

    if (!last_name) {
      errors.last_name = t('LAST_NAME_REQUIRED');
    }

    if (!phone) {
      errors.phone = t('PHONE_REQUIRED');
    }

    this.setState({
      errors,
      notification: {},
    });

    return errors;
  }

  handleStoreChange = () => {
    this.populateForm();
  };

  handleCarrierUpdated = () => {
    const notification = ToastStore.getLatestNotification();
    this.setState({ notification });
  };

  handleSubmit = () => {
    const errors = this.validate();
    if (!Object.keys(errors).length > 0) {
      const {
        name,
        first_name,
        last_name,
        email,
        hides_prices_from_drivers,
        dot_number,
        vat_number,
        primary_scac_code,
        phone,
        phone_extension,
        street,
        city,
        state,
        zipcode,
        international_address,
        province,
        district,
        sub_district,
        post_code,
        country,
        markets,
        linesOfBusiness,
      } = this.state;

      const trimmedDOT = (dot_number || '').trim();

      const carrier = {
        _id: SessionStore.getUser()._id,
        name,
        first_name,
        last_name,
        email,
        hides_prices_from_drivers,
        dot_number: trimmedDOT,
        vat_number,
        primary_scac_code,
        phone,
        phone_extension,
        markets: markets ?? [],
        linesOfBusiness: linesOfBusiness.map((lob) => lob.slug),
        address: {
          street,
          city,
          state,
          zipcode,
          international_address,
          province,
          district,
          sub_district,
          post_code,
          country,
        },
      };
      CustomersActionCreators.updateCarrier(carrier);
    }
  };

  renderErrors() {
    if (Object.keys(this.state.errors).length > 0) {
      return (
        <Alert type="error">
          <ul>
            {Object.keys(this.state.errors).map((errorKey, idx) => (
              <li key={idx}>{this.state.errors[errorKey]}</li>
            ))}
          </ul>
        </Alert>
      );
    }
    return null;
  }

  renderPhoto() {
    const userId = SessionStore.getUser()._id;
    return (
      <div className="dropdown-wrapper">
        <AvatarWithUpload
          key={userId}
          userId={userId}
          altName={unescapeSpecialChars(this.state.name)}
          style={{
            borderWidth: 2,
            borderColor: '#5cb85c',
            borderStyle: 'dashed',
            borderRadius: 5,
            display: 'inline-block',
            padding: 0,
            textAlign: 'center',
          }}
        />
      </div>
    );
  }

  renderNotification() {
    const notification = this.state.notification || {};
    if (notification.title) {
      const { title, body, level } = notification;
      return (
        <Alert type={level === 'success' ? 'success' : 'error'}>
          <p>{title}</p>
          <p>{body}</p>
        </Alert>
      );
    }
    return null;
  }

  handleMarketsSelect = (markets) => {
    this.setState({ markets: (markets ?? []).map((m) => m._id) });
  };

  getLinesOfBusiness() {
    return getRegionLinesOfBusiness().map((status) => {
      status.selected = this.lineOfBusinessSelected(status.slug);
      return status;
    });
  }

  getSelectedMarkets() {
    return buildMarketMenuItems(this.state.allMarkets, this.state.markets);
  }

  lineOfBusinessSelected(slug) {
    const { linesOfBusiness } = this.state;
    const slugs = linesOfBusiness.map((ev) => ev.slug);
    if (slugs.indexOf(slug) > -1) {
      return true;
    }
    return false;
  }

  handleLinesOfBusinessSelect = (linesOfBusiness) => {
    this.setState({ linesOfBusiness });
  };

  render() {
    const linesOfBusinessMenuItems = this.getLinesOfBusiness();
    const marketsMenuItems = this.getSelectedMarkets();
    const { t } = this.props;
    const address = {
      street: this.state.street,
      city: this.state.city,
      state: this.state.state,
      zipcode: this.state.zipcode,
      international_address: this.state.international_address,
      province: this.state.province,
      district: this.state.district,
      sub_district: this.state.sub_district,
      post_code: this.state.post_code,
      country: this.state.country,
    };

    return (
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-6 mt-10">
          <div className="lg:col-span-3">
            <Sidebar activePage="profile" />
          </div>
          <div className="lg:col-span-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h3 className="my-0">
                    {t('WELCOME')}, &nbsp;
                    {unescapeSpecialChars(this.state.name)}
                  </h3>
                  {this.state.carrier_marketing_identifier && (
                    <span>{this.state.carrier_marketing_identifier}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {this.renderNotification()}
                {this.renderErrors()}
                <div className="grid grid-cols-6 gap-4">
                  <div
                    className="col-span-6 sm:col-span-3"
                    data-validationState={this.getValidationState('name')}
                  >
                    <label className="mb-1 form-label">
                      {titleize(t('COMPANY'))}
                    </label>
                    <input
                      type="text"
                      className="form-field"
                      value={unescapeSpecialChars(this.state.name)}
                      placeholder={t('ENTER_NAME')}
                      onChange={this.getHandleInputChange('name')}
                    />
                  </div>
                  <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                    <div
                      className="col-span-6 sm:col-span-3"
                      data-controlId="dotNumber"
                    >
                      <label className="mb-1 form-label">
                        {t('DOT_NUMBER')}
                      </label>
                      <input
                        type="text"
                        className="form-field"
                        value={this.state.dot_number}
                        placeholder={t('ENTER_DOT_NUMBER')}
                        onChange={this.getHandleInputChange('dot_number')}
                      />
                    </div>
                  </AuthorizationWrapper>
                  <AuthorizationWrapper excludedRegions={[REGIONS.US]}>
                    <div
                      className="col-span-6 sm:col-span-3"
                      data-controlId="dotNumber"
                    >
                      <label className="mb-1 form-label">
                        {t('VAT_REGISTRATION_NUMBER')}
                      </label>
                      <input
                        type="text"
                        className="form-field"
                        value={this.state.vat_number}
                        placeholder={t('ENTER_VAT_NUMBER')}
                        onChange={this.getHandleInputChange('vat_number')}
                      />
                    </div>
                  </AuthorizationWrapper>
                  <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                    <div className="col-span-6">
                      <div className="grid grid-cols-6 gap-4">
                        <div
                          className="col-span-6 sm:col-span-3"
                          data-controlId="primaryScacCode"
                        >
                          <label className="mb-1 form-label">
                            {t('SCAC_CODE')}
                          </label>
                          <input
                            type="text"
                            className="form-field"
                            value={this.state?.primary_scac_code}
                            placeholder={t('ENTER_SCAC_CODE')}
                            onChange={this.getHandleInputChange(
                              'primary_scac_code',
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </AuthorizationWrapper>
                  <div
                    data-controlId="first_name"
                    className="col-span-6 sm:col-span-3"
                    data-validationState={this.getValidationState('first_name')}
                  >
                    <label className="mb-1 form-label">
                      {titleize(t('FIRST_NAME'))}
                    </label>
                    <input
                      type="text"
                      className="form-field"
                      value={unescapeSpecialChars(this.state.first_name)}
                      placeholder={t('ENTER_FIRST_NAME')}
                      onChange={this.getHandleInputChange('first_name')}
                    />
                  </div>
                  <div
                    data-controlId="last_name"
                    className="col-span-6 sm:col-span-3"
                    data-validationState={this.getValidationState('last_name')}
                  >
                    <label className="mb-1 form-label">
                      {titleize(t('LAST_NAME'))}
                    </label>
                    <input
                      type="text"
                      className="form-field"
                      value={unescapeSpecialChars(this.state.last_name)}
                      placeholder={t('ENTER_LAST_NAME')}
                      onChange={this.getHandleInputChange('last_name')}
                    />
                  </div>
                </div>
                <AddressForm
                  address={address}
                  getHandleInputChange={this.getHandleInputChange}
                  getValidationState={this.getValidationState}
                />
                <div className="grid grid-cols-6 gap-4 mt-3">
                  <div
                    data-controlId="email"
                    className="col-span-6 sm:col-span-4"
                    data-validationState={this.getValidationState('email')}
                  >
                    <label className="mb-1 form-label">
                      {titleize(t('EMAIL'))}
                    </label>
                    <input
                      type="email"
                      className="form-field"
                      value={this.state.email}
                      placeholder={t('ENTER_EMAIL')}
                      onChange={this.getHandleInputChange('email')}
                    />
                  </div>
                  <div
                    data-controlId="phone"
                    className="col-span-6 sm:col-span-3"
                    data-validationState={this.getValidationState('phone')}
                  >
                    <label className="mb-1 form-label">
                      {titleize(t('PHONE'))}
                    </label>
                    <PhoneField
                      id="carrier-phone"
                      value={this.state.phone}
                      onChange={this.handlePhoneChange}
                    />
                  </div>
                  <div
                    data-controlId="phone_extension"
                    className="col-span-6 sm:col-span-3"
                    data-validationState={this.getValidationState(
                      'phone_extension',
                    )}
                  >
                    <CargomaticInput
                      type="text"
                      className="mb-1 form-label"
                      label={titleize(t('EXT'))}
                      inputClassNames="form-field"
                      value={this.state.phone_extension}
                      placeholder={t('ENTER_PHONE_NUMBER_EXTENSION')}
                      onChange={this.getHandleInputChange('phone_extension')}
                    />
                  </div>
                  {this.state.allMarkets.length > 0 && (
                    <div
                      data-controlId="markets"
                      className="col-span-6 sm:col-span-4"
                    >
                      <label className="mb-1 form-label">
                        {titleize(t('MARKETS', { count: 2 }))}
                      </label>
                      <Multiselect
                        label={titleize(t('MARKETS', { count: 2 }))}
                        items={marketsMenuItems}
                        onSelect={this.handleMarketsSelect}
                        searchKey="name"
                        valueKey="_id"
                      />
                    </div>
                  )}
                  <div
                    data-controlId="LOBs"
                    className="col-span-6 sm:col-span-4"
                  >
                    <label className="mb-1 form-label">
                      {t('LINES_OF_BUSINESS')}
                    </label>
                    <Multiselect
                      label={t('LINES_OF_BUSINESS')}
                      items={linesOfBusinessMenuItems}
                      onSelect={this.handleLinesOfBusinessSelect}
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <button
                  className="button button-green"
                  onClick={this.handleSubmit}
                >
                  {titleize(t('SAVE'))}
                </button>
              </CardFooter>
            </Card>
            <AccountContacts
              className="mt-6"
              contacts={this.state.contacts}
              userId={this.state.userId}
            />
            <CarrierAssociatedAccounts
              className="mt-12"
              onAccessRequest={handleAccessRequest}
              userId={this.state.userId}
            />
          </div>
          <div className="lg:col-span-3 space-y-4">
            {this.renderPhoto()}
            <div style={{ marginTop: '1em' }}>
              <Button
                variant="outline"
                onClick={() => this.setState({ showChangePasswordModal: true })}
              >
                {t('CHANGE_PASSWORD')}
              </Button>
            </div>
          </div>
          <ChangePasswordModal
            show={this.state.showChangePasswordModal}
            onHide={() => this.setState({ showChangePasswordModal: false })}
            onSave={onUpdatePassword}
          />
        </div>
      </Container>
    );
  }
}

export default withTranslation()(CarrierCenter);
